// Facade + browser entry: the pure export engine moved to
// supabase/functions/_shared/excel-export/exportCore.ts so the
// generate-survey-export edge function can share it. This module keeps every
// historical import path alive and re-adds the browser-only concerns —
// reviewer-evidence fetch (caller's RLS identity), the gs:// proxy (caller's
// Firebase token), and the download trigger.

import type { AttributeField, TemplateField } from "@excel-export/types.ts";
import type {
  ExcelCellMapping,
  MappingContext,
  UnitSourceAsset,
} from "@excel-export/exportCore.ts";
import { generateSurveyExcel } from "@excel-export/exportCore.ts";
import type { SlotAsset } from "@excel-export/summaryPhotoFill.ts";
import { triggerBlobDownload } from "@/features/exports/services/_helpers";

export * from "@excel-export/exportCore.ts";
export type { AttributeField, TemplateField } from "@excel-export/types.ts";

/**
 * Reads the picked .xlsx template, stamps the survey's answers into the mapped
 * cells, and downloads the result. Returns counts for user feedback.
 */
export async function exportSurveyToExcelTemplate(params: {
  file: File;
  templateFields: TemplateField[];
  fields: AttributeField[];
  propertyName?: string | null;
  propertyAddress?: string | null;
  /** Form-level property/static mappings, stamped alongside the answers. */
  excelCellMappings?: ExcelCellMapping[];
  mappingContext?: MappingContext;
  /** Property assets (full-shot/nameplate photo pairs) for the Summary photo slots. */
  assets?: Array<SlotAsset & UnitSourceAsset>;
  /** The survey row's prod_data — answers for questions absent from the tree. */
  prodData?: Record<string, unknown> | null;
  /** property_attributes.id — used to fetch live reviewer evidence (photos). */
  surveyId?: string;
  /** Sheet for writes without a per-question excelTemplateSheet; defaults to the first sheet. */
  defaultSheet?: string | null;
  /** "download" (default) triggers the browser download; "blob" returns the file instead. */
  deliver?: "download" | "blob";
  /** Stage updates for progress UI. */
  onProgress?: (message: string) => void;
}): Promise<{ written: number; mapped: number; data?: Blob; filename?: string }> {
  const progress = params.onProgress ?? (() => {});

  // Browser-only inputs, lazily resolved so the pure chain stays test-loadable.
  progress("Loading reviewer photos…");
  const { fetchReviewerEvidence } = await import("./fetchReviewerEvidence");
  const { buildGcsProxy } = await import("./qaEvidenceProxy");
  const reviewerEvidence = params.surveyId
    ? await fetchReviewerEvidence(params.surveyId).catch(() => new Map<string, unknown[]>())
    : undefined;

  const result = await generateSurveyExcel({
    template: { buf: await params.file.arrayBuffer(), name: params.file.name },
    templateFields: params.templateFields,
    fields: params.fields,
    propertyName: params.propertyName,
    propertyAddress: params.propertyAddress,
    excelCellMappings: params.excelCellMappings,
    mappingContext: params.mappingContext,
    assets: params.assets,
    prodData: params.prodData,
    reviewerEvidence,
    gcsProxy: await buildGcsProxy(),
    defaultSheet: params.defaultSheet,
    onProgress: params.onProgress,
  });

  if (params.deliver === "blob" || !result.data || !result.filename) return result;
  triggerBlobDownload(result.data, result.filename);
  return { written: result.written, mapped: result.mapped, filename: result.filename };
}
